/**
 * Authentication Context
 * Manages user authentication state and provides auth methods
 * Supports both guest mode and registered users
 */

import { createContext, useContext, useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { initializeUserDocument, getUserQuotaStatus } from '../utils/userQuota';
import { hasGuestAnalysisRemaining, getGuestAnalysisRemaining } from '../utils/guestMode';
import { trackEvent } from '@/lib/analytics';

const AuthContext = createContext({});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false); // Start with false to prevent blocking
  const [userQuota, setUserQuota] = useState(null);
  const [frameworkQuotas, setFrameworkQuotas] = useState(null);
  const [isGuest, setIsGuest] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setIsGuest(!firebaseUser);

      if (firebaseUser) {
        try {
          // Initialize user document if needed (with validation and retry)
          await initializeUserDocument(firebaseUser.uid, firebaseUser.email);

          // Load user quota (both frameworks)
          const quotas = await getUserQuotaStatus(firebaseUser.uid);
          console.log('User quota loaded:', quotas);

          // Validate quota data
          if (!quotas?.current || quotas.current.remaining === undefined || isNaN(quotas.current.remaining)) {
            console.error('Invalid quota data received:', quotas);
            // Set a safe default to prevent UI from breaking
            const fallback = { hasQuota: true, remaining: 10, count: 0, limit: 10, framework: 'current' };
            setUserQuota(fallback);
            setFrameworkQuotas({ current: fallback, advanced: { ...fallback, framework: 'advanced', limit: 3, remaining: 3 } });
          } else {
            setFrameworkQuotas(quotas);
            setUserQuota(quotas.current);
          }
        } catch (error) {
          console.error('Error initializing user:', error);
          // Set default quota on error to prevent UI from breaking
          setUserQuota({ hasQuota: true, remaining: 10, count: 0, limit: 10, error: error.message });
          setFrameworkQuotas(null);
        }
      } else {
        setUserQuota(null);
        setFrameworkQuotas(null);
      }
    });

    return unsubscribe;
  }, []);

  /**
   * Send sign-in link to email (passwordless auth)
   */
  const sendSignInLink = async (email) => {
    const actionCodeSettings = {
      url: window.location.origin + '/auth/verify',
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      // Save email to localStorage for verification
      window.localStorage.setItem('emailForSignIn', email);
      return { success: true };
    } catch (error) {
      console.error('Error sending sign-in link:', error);
      return { success: false, error: error.message, code: error.code };
    }
  };

  /**
   * Complete sign-in with email link
   * If email is not provided and not in localStorage, returns { needsEmail: true }
   */
  const completeSignIn = async (url, providedEmail = null) => {
    if (!isSignInWithEmailLink(auth, url)) {
      return { success: false, error: 'Invalid sign-in link' };
    }

    let email = providedEmail || window.localStorage.getItem('emailForSignIn');

    if (!email) {
      // User opened link on different device, needs to provide email
      return { success: false, needsEmail: true };
    }

    try {
      const result = await signInWithEmailLink(auth, email, url);
      window.localStorage.removeItem('emailForSignIn');

      trackEvent('signup_completed');
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Error completing sign-in:', error);
      return { success: false, error: error.message, code: error.code };
    }
  };

  /**
   * Sign in with Google
   */
  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      trackEvent('signup_completed', { method: 'google' });
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Error signing in with Google:', error);
      return { success: false, error: error.message, code: error.code };
    }
  };

  /**
   * Sign out user
   */
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUserQuota(null);
      return { success: true };
    } catch (error) {
      console.error('Error signing out:', error);
      return { success: false, error: error.message, code: error.code };
    }
  };

  /**
   * Refresh user quota
   */
  const refreshQuota = async () => {
    if (user) {
      try {
        const quotas = await getUserQuotaStatus(user.uid);
        setFrameworkQuotas(quotas);
        setUserQuota(quotas?.current || null);
        return quotas;
      } catch (error) {
        console.error('Error refreshing quota:', error);
        return null;
      }
    }
    return null;
  };

  /**
   * Check if user can perform analysis
   */
  const canAnalyze = (frameworkType = 'current') => {
    if (isGuest) {
      return hasGuestAnalysisRemaining(frameworkType);
    }
    const normalized = frameworkType === 'advanced' ? 'advanced' : 'current';
    const quota = frameworkQuotas?.[normalized] || userQuota;
    return quota?.hasQuota || false;
  };

  /**
   * Get remaining analyses count
   */
  const getRemainingAnalyses = (frameworkType = 'current') => {
    if (isGuest) {
      return getGuestAnalysisRemaining(frameworkType);
    }
    const normalized = frameworkType === 'advanced' ? 'advanced' : 'current';
    const quota = frameworkQuotas?.[normalized] || userQuota;
    return quota?.remaining || 0;
  };

  const value = {
    user,
    loading,
    isGuest,
    userQuota,
    frameworkQuotas,
    sendSignInLink,
    signInWithGoogle,
    completeSignIn,
    signOut,
    refreshQuota,
    canAnalyze,
    getRemainingAnalyses,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
